import {
  FluidCurrencyFormatOptions,
  FluidSize,
  FluidStyleSize,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FluidFormatters } from '@lmig/fluid-core/lib/utils/formatting.utils';

import { Utils } from '../utils';
import {
  CurrencyOptions,
  currencyOptions,
  formatAsCurrency,
} from './ii8n.utils';

export const formatCurrency = (
  value: string | number,
  currencyFormatOptions: FluidCurrencyFormatOptions
) => {
  if (currencyFormatOptions.roundingMethod && !isNaN(Number(value))) {
    value = FluidFormatters.applyRounding(
      value as number,
      currencyFormatOptions.roundingMethod
    );
  }

  const _currencyOptions: CurrencyOptions = currencyOptions(
    currencyFormatOptions.currencySymbol,
    currencyFormatOptions.maximumFractionDigits
  );
  if (typeof value === 'string') {
    value = parseFloat(value);
  }
  return formatAsCurrency(
    value,
    null,
    _currencyOptions.currency,
    _currencyOptions,
    false
  );
};

/**
 * Converts FluidStyleSize values into CSS inline styles. Decimal numbers are
 * converted to percentages, integers are converted to pixel values, & strings
 * are returned as-is (since they should already be formatted as CSS inline
 * style values). FLUID Enum values are NOT returned since they're used to
 * apply CSS classes to the Container.
 * @param value - the Prop value to convert to inline style value
 */
export const parseSizeValue = (value: FluidStyleSize): string => {
  switch (true) {
    // Do nothing if value is undefined or is an Enum value (Enums added as classes)
    case isNullOrUndefined(value):
    case Utils.isFluidEnum<FluidSize>(value, FluidSize):
      return;
    // Return percentage if value is a decimal number between -1 & 1, but NOT zero
    case Utils.isNumber(value) &&
      (value as number) >= -1 &&
      (value as number) < 0:
    case Utils.isNumber(value) &&
      (value as number) <= 1 &&
      (value as number) > 0:
      return `${(value as number) * 100}%`;
    // Return pixel value if number is integer less than -1 or greater than 1
    case Utils.isNumber(value) && (value as number) < -1:
    case Utils.isNumber(value) && (value as number) > 1:
      return `${value}px`;
    // Return custom CSS string (if it's invalid, Stencil will NOT apply it as inline style)
    default:
      return String(value);
  }
};

/**
 * Converts shorthand string representations of numbers into the corresponding numerical value
 * @param value - the Prop value to convert to numerical value
 */
export const convertShortHandToNumber = (value: string | number): number => {
  value = String(value === 'v' ? 0 : value);
  const number = parseFloat(value);
  const unit = value.charAt(value.length - 1).toLowerCase();
  switch (unit) {
    case 'k': {
      return number * 1_000;
    }
    case 'm': {
      return number * 1_000_000;
    }
    case 'b': {
      return number * 1_000_000_000;
    }
    case '.': {
      return 0;
    }
    default: {
      return number;
    }
  }
};
